// ==========================================================================
// EMPLOYEES MANAGEMENT MODULE
// WuRuPayroll - Employee directory, CRUD, attachments, disciplinary & archive
// ==========================================================================

import {
  getEmployees,
  addEmployee,
  updateEmployee,
  deleteEmployee,
  getResignedArchiveEmployees,
  uploadEmployeeDocument,
  deleteEmployeeAttachment,
  getAttachmentBlob,
  getTimesheet,
  saveTimesheet
} from '../firebase-db.js';

import { escapeHtml, escapeAttr, escapeJsParam } from '../utils/sanitize.js';
import { toast, showConfirm } from '../utils/toast.js';
import {
  isEmployeeActiveInMonth,
  getEmployeeDeductionSettings
} from './payroll-calc.js';

// Safe proxy for global application state
const state = new Proxy({}, {
  get(target, prop) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    return s[prop];
  },
  set(target, prop, value) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    s[prop] = value;
    return true;
  }
});

// Safe wrappers for app routing, context and permissions
const navigateTo = (page) => {
  if (typeof window !== 'undefined' && window.navigateTo) {
    return window.navigateTo(page);
  }
};

const loadCompanyContext = (forceLoadHolidays) => {
  if (typeof window !== 'undefined' && window.loadCompanyContext) {
    return window.loadCompanyContext(forceLoadHolidays);
  }
};

const checkPermission = (action) => {
  if (typeof window !== 'undefined' && window.checkPermission) {
    return window.checkPermission(action);
  }
  return true;
};

const canViewSalary = (emp) => {
  if (typeof window !== 'undefined' && window.canViewSalary) {
    return window.canViewSalary(emp);
  }
  return true;
};

// Employees Page rendering & editing (Requirement 2, 20, 21, 23, 28, 29)
function renderEmployeesPage() {
  document.getElementById('page-display-title').textContent = 'ข้อมูลและบันทึกเวลาทำงานพนักงาน';
  document.getElementById('page-display-desc').textContent = `จัดการประวัติ ความรับผิดชอบ และบันทึกการ ขาด ลา มาสาย การเบิกล่วงหน้ารายบุคคลสำหรับเดือน ${state.selectedMonth}`;

  const wrapper = document.getElementById('page-content-wrapper');

  const activeTab = state.activeEmployeeTab || 'active';

  // Filter employees based on activeTab and month
  let filteredEmps = [];
  if (activeTab === 'active') {
    filteredEmps = state.employees.filter(emp => isEmployeeActiveInMonth(emp, state.selectedMonth));
  } else {
    filteredEmps = state.employees.filter(emp => emp.status === 'resigned');
  }

  let html = `
    <div class="glass-card" style="margin-bottom: 25px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 15px;">
      <h3 style="font-weight: 700; color: var(--primary-dark);">รายชื่อพนักงาน</h3>
      <div style="display: flex; gap: 10px; flex-wrap: wrap;">
        ${window.checkPermission('reorder_employees') ? `
          <button class="btn ${state.isReorderMode ? 'btn-danger' : 'btn-secondary'}" onclick="toggleReorderMode()">
            ${state.isReorderMode ? '🔒 ล็อคตำแหน่ง' : '✏️ จัดตำแหน่งการ์ด'}
          </button>
        ` : ''}
        <button class="btn btn-primary" onclick="showAddEmployeeModal()">${window.checkPermission('add_employee') ? '➕ เพิ่มพนักงานใหม่' : '🔒 ไม่มีสิทธิ์เพิ่มพนักงาน'}</button>
      </div>
    </div>

    <!-- Tab switcher -->
    <div class="calendar-modes" style="margin-bottom: 20px; display: inline-flex;">
      <button class="calendar-mode-btn ${activeTab === 'active' ? 'active' : ''}" id="btn-emp-active" onclick="switchEmployeeTab('active')">👥 พนักงานที่ทำงาน</button>
      <button class="calendar-mode-btn ${activeTab === 'resigned' ? 'active' : ''}" id="btn-emp-resigned" onclick="switchEmployeeTab('resigned')">📁 พนักงานที่ลาออกแล้ว</button>
    </div>
  `;

  // Show "Filter resigned for 2 years" button ONLY in resigned tab
  if (activeTab === 'resigned') {
    html += `
      <div style="margin-bottom: 20px;">
        <button class="btn btn-secondary" onclick="showResignedArchiveModal()">📁 คัดกรองผู้ลาออกครบ 2 ปี</button>
      </div>
    `;
  }

  html += `<div class="employee-grid">`;

  if (filteredEmps.length === 0) {
    html += `
      <div class="glass-card text-center" style="grid-column: 1/-1; padding: 50px;">
        <h4 style="color: var(--text-sub);">ไม่พบข้อมูลลูกน้องในกลุ่มนี้สำหรับงวดบัญชีนี้</h4>
      </div>
    `;
  } else {
    filteredEmps.forEach(emp => {
      let typeText = '';
      let typeBg = 'var(--gray-200)';
      let typeColor = 'var(--text-main)';

      if (emp.type === 'salary') {
        typeText = 'พนักงานเงินเดือนประจำ';
        typeBg = '#e0e7ff';
        typeColor = '#4338ca';
      } else if (emp.type === 'daily') {
        typeText = 'พนักงานรายวัน';
        typeBg = '#fce7f3';
        typeColor = '#be185d';
      } else {
        typeText = 'ฟรีแลนซ์';
        typeBg = '#f3e8ff';
        typeColor = '#7e22ce';
      }

      const targetResignDate = emp.resignDate || emp.resignedDate;
      const resignedTag = emp.status === 'resigned' ? `<span class="badge badge-danger">ลาออกแล้ว ${targetResignDate ? `(วันสุดท้าย: ${escapeHtml(targetResignDate)})` : ''}</span>` : `<span class="badge badge-success">กำลังทำงาน</span>`;

      const internRibbon = emp.isIntern ? `<div style="position: absolute; top: 22px; right: -45px; width: 160px; background: var(--warning); color: white; padding: 5px 0; transform: rotate(45deg); font-size: 0.72rem; font-weight: bold; box-shadow: 0 2px 5px rgba(0,0,0,0.2); text-align: center; z-index: 10; letter-spacing: 0.5px;">พนักงานฝึกงาน</div>` : '';

      // Check active warning
      let hasActiveWarning = false;
      if (emp.disciplinaryRecords && emp.disciplinaryRecords.length > 0) {
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        hasActiveWarning = emp.disciplinaryRecords.some(r => r.type === 'warning' && new Date(r.issueDate) >= oneYearAgo);
      }

      let warningStyle = '';
      let warningAlert = '';
      if (hasActiveWarning) {
        warningStyle = 'border: 2px solid #a855f7;'; // Tailwind purple-500
        warningAlert = '<div style="color: #9333ea; font-size: 0.85rem; margin-top: 4px; font-weight: normal; line-height: 1.2;">⚠️ (มีหนังสือเตือน)</div>';
      }

      const shakeClass = state.isReorderMode ? 'shaking' : '';

      html += `
        <div class="glass-card employee-card ${shakeClass}" 
             data-emp-id="${escapeAttr(emp.id)}" 
             ${window.checkPermission('reorder_employees') && state.isReorderMode ? 'draggable="true" ondragstart="handleEmpDragStart(event)" ondragover="handleEmpDragOver(event)" ondragleave="handleEmpDragLeave(event)" ondrop="handleEmpDrop(event)" ondragend="handleEmpDragEnd(event)"' : ''} 
             style="display: flex; flex-direction: column; justify-content: space-between; gap: 15px; position: relative; overflow: hidden; ${warningStyle}">
          
          ${internRibbon}

          <div style="z-index: 1; padding-right: ${emp.isIntern ? '45px' : '0px'};">
            <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;">
              <div>
                <h4 style="font-weight: 700; font-size: 1.15rem; color: var(--primary-dark); margin: 0;">${escapeHtml(emp.realName)}</h4>
                ${warningAlert}
              </div>
              <span class="badge badge-info">${escapeHtml(emp.nickname)}</span>
            </div>

            <div style="margin-bottom: 6px;">
              <span class="badge" style="background: ${typeBg}; color: ${typeColor};">🛠️ ${typeText}</span>
            </div>
            <div>
              ${resignedTag}
            </div>
          </div>
          
          <div style="border-top: 1px solid var(--gray-300); padding-top: 15px; display: flex; flex-direction: column; gap: 8px;">
            <div style="display: flex; gap: 8px; width: 100%;">
              <button class="btn btn-secondary btn-sm" style="flex: 1;" onclick="viewEmployeeDetails('${escapeJsParam(emp.id)}')">🔍 ดูข้อมูลและไฟล์</button>
              ${(window.checkPermission('edit_employee_general') || window.checkPermission('edit_employee_salary')) ? `<button class="btn btn-primary btn-sm" onclick="editEmployee('${escapeJsParam(emp.id)}')">✏️ แก้ไข</button>` : ''}
            </div>
            <button class="btn btn-primary btn-sm" style="width: 100%; display: flex; align-items: center; justify-content: center; gap: 5px;" onclick="openEmployeeTimesheetPage('${escapeJsParam(emp.id)}')">🗓️ สรุปตารางทำงานทั้งเดือน</button>
          </div>
        </div>
      `;
    });
  }

  html += `</div>`;
  wrapper.innerHTML = html;

  // Asynchronously check for 3 consecutive absences to highlight cards
  filteredEmps.forEach(emp => {
    getTimesheet(emp.id, state.selectedMonth).then(ts => {
      let hasConsecutive = false;
      if (ts && ts.days) {
        let count = 0;
        for (let d = 1; d <= 31; d++) {
          const dayData = ts.days[d];
          if (dayData && dayData.status === 'absence') {
            count++;
            if (count >= 3) {
              hasConsecutive = true;
              break;
            }
          } else if (dayData && dayData.status && dayData.status !== 'day_off' && dayData.status !== 'not_started' && dayData.status !== 'holiday' && dayData.status !== 'holiday_selected') {
            count = 0;
          }
        }
      }

      if (hasConsecutive) {
        const card = document.querySelector(`.employee-card[data-emp-id="${emp.id}"]`);
        if (card) {
          // Change border to red alert colors
          card.style.border = '2px solid #ef4444'; // Tailwind red-500

          // Append alert text next to the name
          const title = card.querySelector('h4');
          if (title && !title.innerHTML.includes('ขาดงาน 3 วันติด')) {
            title.innerHTML += ' <span style="color: #dc2626; font-size: 0.8rem; margin-left: 5px;">🚨 (ขาดงาน 3 วันติด)</span>';
          }

          // Inject Termination button if not exists
          if (!card.querySelector('.btn-terminate-direct')) {
            const btnContainer = card.querySelector('div[style*="border-top: 1px solid"]');
            if (btnContainer) {
              const termBtn = document.createElement('button');
              termBtn.className = 'btn btn-danger btn-sm btn-terminate-direct';
              termBtn.style.width = '100%';
              termBtn.style.marginTop = '8px';
              termBtn.style.animation = 'pulse 2s infinite';
              termBtn.innerHTML = '🚨 ออกหนังสือเลิกจ้างทันที';
              termBtn.onclick = function () { openDisciplinaryModal(emp.id, 'termination'); };
              btnContainer.appendChild(termBtn);
            }
          }
        }
      }
    }).catch(err => console.error("Error fetching timesheet for absence check:", err));
  });
}

// Drag and Drop reordering handlers for employee cards
function handleEmpDragStart(e) {
  e.dataTransfer.setData('text/plain', e.currentTarget.getAttribute('data-emp-id'));
  e.currentTarget.classList.add('dragging');
  e.dataTransfer.effectAllowed = 'move';
};

function handleEmpDragOver(e) {
  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';
  const draggingCard = document.querySelector('.employee-card.dragging');
  const targetCard = e.currentTarget;
  if (draggingCard && targetCard && draggingCard !== targetCard) {
    targetCard.classList.add('drag-over');
  }
};

function handleEmpDragLeave(e) {
  e.currentTarget.classList.remove('drag-over');
};

async function handleEmpDrop(e) {
  e.preventDefault();
  e.currentTarget.classList.remove('drag-over');

  const draggedId = e.dataTransfer.getData('text/plain');
  const targetId = e.currentTarget.getAttribute('data-emp-id');

  if (!draggedId || !targetId || draggedId === targetId) return;

  const draggedIndex = state.employees.findIndex(emp => emp.id === draggedId);
  const targetIndex = state.employees.findIndex(emp => emp.id === targetId);

  if (draggedIndex !== -1 && targetIndex !== -1) {
    const [draggedEmp] = state.employees.splice(draggedIndex, 1);
    state.employees.splice(targetIndex, 0, draggedEmp);

    state.employees.forEach((emp, index) => {
      emp.sortOrder = index;
    });

    // Re-render instantly
    renderEmployeesPage();

    try {
      await Promise.all(state.employees.map(emp => updateEmployee(emp.id, { sortOrder: emp.sortOrder })));
      console.log("New employee sorting saved to Firebase.");
    } catch (err) {
      console.error("Failed to save reordered employees:", err);
    }
  }
};

function handleEmpDragEnd(e) {
  document.querySelectorAll('.employee-card').forEach(card => {
    card.classList.remove('dragging');
    card.classList.remove('drag-over');
  });
};

function switchEmployeeTab(tab) {
  state.activeEmployeeTab = tab;
  renderEmployeesPage();
};

function toggleReorderMode() {
  state.isReorderMode = !state.isReorderMode;
  renderEmployeesPage();
};

// Proxy to navigate to timesheet page
function openEmployeeTimesheetPage(empId) {
  state.activeEditingEmployeeId = empId;
  state.timesheetReturnPage = (state.activePage && state.activePage !== 'timesheet') ? state.activePage : 'dashboard';
  navigateTo('timesheet');
};

// Modal functions for adding / editing employee
function showAddEmployeeModal() {
  if (!window.checkPermission('add_employee')) {
    alert("ขออภัย! คุณไม่มีสิทธิ์แก้ไขหรือเพิ่มข้อมูลพนักงานของบริษัทนี้");
    return;
  }

  // Render employee form modal
  renderEmployeeFormModal(null);
};

function editEmployee(empId) {
  if (!window.checkPermission('edit_employee_general') && !window.checkPermission('edit_employee_salary')) {
    alert("ขออภัย! คุณไม่มีสิทธิ์แก้ไขข้อมูลพนักงานของบริษัทนี้");
    return;
  }
  const emp = state.employees.find(e => e.id === empId);
  renderEmployeeFormModal(emp);
};

function toggleCustomTaxRate(useDefault) {
  const input = document.getElementById('emp-tax-rate');
  if (input) {
    input.disabled = useDefault;
    if (useDefault) {
      input.value = state.activeCompanySettings.withholdingTaxRate || 3;
    }
  }
};

function toggleCustomSSRate(useDefault) {
  const input = document.getElementById('emp-ss-rate');
  if (input) {
    input.disabled = useDefault;
    if (useDefault) {
      input.value = state.activeCompanySettings.socialSecurityRate || 5;
    }
  }
};

function toggleInternStatus(checked) {
  const ssCheckbox = document.getElementById('emp-ss-enabled');
  const taxCheckbox = document.getElementById('emp-tax-enabled');
  if (checked) {
    if (ssCheckbox) {
      ssCheckbox.checked = false;
      ssCheckbox.dispatchEvent(new Event('change'));
    }
    if (taxCheckbox) {
      taxCheckbox.checked = true;
      taxCheckbox.dispatchEvent(new Event('change'));
    }
  }
};

function toggleDiligenceFields(checked) {
  const fields = document.getElementById('diligence-fields');
  if (fields) {
    fields.style.display = checked ? 'block' : 'none';
  }
};

function toggleCustomDiligenceAmount(useDefault) {
  const input = document.getElementById('emp-diligence-amount');
  if (input) {
    input.disabled = useDefault;
    if (useDefault) {
      input.value = state.activeCompanySettings.diligenceAllowanceAmount !== undefined ? state.activeCompanySettings.diligenceAllowanceAmount : 1000;
    }
  }
};

function renderEmployeeFormModal(emp = null) {
  const isEdit = !!emp;
  const daysOfWeek = ['วันอาทิตย์', 'วันจันทร์', 'วันอังคาร', 'วันพุธ', 'วันพฤหัสบดี', 'วันศุกร์', 'วันเสาร์'];

  let salaryLabelText = 'อัตราจ้าง (เงินเดือนประจำ) *';
  if (isEdit) {
    if (emp.type === 'daily') {
      salaryLabelText = 'อัตราจ้าง (รายวัน) *';
    } else if (emp.type === 'freelance') {
      salaryLabelText = 'อัตราจ้าง (เหมาจ่ายรายวัน) *';
    }
  }

  const dedSettings = getEmployeeDeductionSettings(emp, state.activeCompanySettings);

  const canEditGeneral = window.checkPermission('edit_employee_general') || (!isEdit && window.checkPermission('add_employee'));
  const canEditSalary = window.checkPermission('edit_employee_salary') || (!isEdit && window.checkPermission('add_employee'));
  const canAssignRole = window.checkPermission('assign_employee_roles');

  // Generate modal skeleton
  let modalOverlay = document.getElementById('employee-modal-overlay');
  if (!modalOverlay) {
    modalOverlay = document.createElement('div');
    modalOverlay.id = 'employee-modal-overlay';
    modalOverlay.className = 'modal-overlay';
    document.body.appendChild(modalOverlay);
  }


  modalOverlay.innerHTML = `
    <div class="modal-container" style="max-width: 900px;">
      <div class="modal-header">
        <h3>${isEdit ? 'แก้ไขข้อมูลพนักงาน' : 'เพิ่มพนักงานใหม่'}</h3>
        <button class="btn-close-modal" onclick="closeEmployeeModal()">&times;</button>
      </div>
      <div class="modal-body">
        <form id="employee-detail-form">
          <div class="form-row">
            <div class="form-group">
              <label>ชื่อจริง - นามสกุล *</label>
              <input type="text" id="emp-realName" value="${isEdit ? escapeAttr(emp.realName) : ''}" required ${!canEditGeneral ? 'disabled' : ''}>
            </div>
            <div class="form-group">
              <label>ชื่อเล่น *</label>
              <input type="text" id="emp-nickname" value="${isEdit ? escapeAttr(emp.nickname) : ''}" required ${!canEditGeneral ? 'disabled' : ''}>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>ตำแหน่ง (Position) *</label>
              <input type="text" id="emp-position" value="${isEdit ? escapeAttr(emp.position || '') : ''}" placeholder="เช่น พนักงานขาย, บัญชี, ผู้จัดการ" required ${!canEditGeneral ? 'disabled' : ''}>
            </div>
            <div class="form-group">
              <label>แผนก (Department) (ไม่บังคับ)</label>
              <input type="text" id="emp-department" value="${isEdit ? escapeAttr(emp.department || '') : ''}" placeholder="เช่น ฝ่ายขาย, คลังสินค้า" ${!canEditGeneral ? 'disabled' : ''}>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>เลขบัตรประจำตัวประชาชน (13 หลัก) *</label>
              <input type="text" id="emp-citizenId" value="${isEdit ? escapeAttr(emp.citizenId) : ''}" maxlength="13" required ${!canEditGeneral ? 'disabled' : ''}>
            </div>
            <div class="form-group">
              <label>ธนาคาร (ไม่บังคับ)</label>
              <input type="text" id="emp-bankName" list="thai-banks-list" value="${isEdit ? escapeAttr(emp.bankName || '') : ''}" placeholder="เลือกหรือพิมพ์ชื่อธนาคาร" ${!canEditGeneral ? 'disabled' : ''}>
              <datalist id="thai-banks-list">
                <option value="ธนาคารกสิกรไทย (KBANK)">
                <option value="ธนาคารไทยพาณิชย์ (SCB)">
                <option value="ธนาคารกรุงเทพ (BBL)">
                <option value="ธนาคารกรุงไทย (KTB)">
                <option value="ธนาคารกรุงศรีอยุธยา (BAY)">
                <option value="ธนาคารทหารไทยธนชาต (TTB)">
                <option value="ธนาคารออมสิน (GSB)">
                <option value="ธ.ก.ส. (BAAC)">
                <option value="ธนาคารยูโอบี (UOB)">
                <option value="ธนาคารซีไอเอ็มบี ไทย (CIMB)">
                <option value="ธนาคารเกียรตินาคินภัทร (KKP)">
                <option value="ธนาคารทิสโก้ (TISCO)">
                <option value="ธนาคารแลนด์ แอนด์ เฮ้าส์ (LH Bank)">
              </datalist>
            </div>
            <div class="form-group">
              <label>เลขบัญชีธนาคาร (ไม่บังคับ)</label>
              <input type="text" id="emp-bankAccount" value="${isEdit ? escapeAttr(emp.bankAccount || '') : ''}" placeholder="เช่น 123-4-56789-0" ${!canEditGeneral ? 'disabled' : ''}>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>สถานะพนักงาน *</label>
              <select id="emp-status" onchange="toggleEmployeeStatusFields(this.value)" ${!canEditGeneral ? 'disabled' : ''} style="font-weight: 600;">
                <option value="active" ${(!isEdit || emp.status !== 'resigned') ? 'selected' : ''}>🟢 กำลังทำงาน (Active)</option>
                <option value="resigned" ${(isEdit && emp.status === 'resigned') ? 'selected' : ''}>🔴 ลาออกแล้ว (Resigned)</option>
              </select>
            </div>
            <div class="form-group">
              <label>วันที่เริ่มงาน *</label>
              <input type="date" id="emp-startDate" value="${isEdit ? emp.startDate : new Date().toISOString().split('T')[0]}" required ${!canEditGeneral ? 'disabled' : ''}>
            </div>
          </div>

          <!-- Resignation details group -->
          <div id="resignation-details-box" style="${(isEdit && emp.status === 'resigned') ? 'display: block;' : 'display: none;'} background: rgba(230, 57, 70, 0.05); border: 1px solid rgba(230, 57, 70, 0.3); border-radius: var(--border-radius-md); padding: 15px; margin-bottom: 15px;">
            <div class="form-row">
              <div class="form-group" style="flex: 1;">
                <label style="color: var(--danger); font-weight: 700;">📅 วันทำงานวันสุดท้าย (วันที่มีผลลาออก) *</label>
                <input type="date" id="emp-resignDate" value="${isEdit ? (emp.resignDate || emp.resignedDate || '') : ''}" ${!canEditGeneral ? 'disabled' : ''}>
                <small style="color: var(--text-sub); display: block; margin-top: 4px;">* ระบบจะคำนวณเงินเดือนจนถึงวันที่ระบุ และหลังจากวันนี้จะถือว่าลาออก</small>
              </div>
              <div class="form-group" id="resign-refund-group" style="flex: 1; ${(isEdit && emp.requiresSecurityDeposit) ? '' : 'display: none;'}">
                <label style="font-weight: 700;">จำนวนเงินคืนประกันสะสม (บาท)</label>
                <input type="number" id="emp-securityDepositRefundAmount" value="${isEdit && emp.securityDepositRefundAmount !== undefined ? emp.securityDepositRefundAmount : (isEdit ? (emp.securityDepositAmount || 0) : 0)}" min="0" placeholder="0 หากริบเงินประกัน">
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group" style="flex:1;">
              <label>วันที่เริ่มสิทธิ์ประกันสังคม (ไม่บังคับ)</label>
              <input type="date" id="emp-socialSecurityStartDate" value="${isEdit ? (emp.socialSecurityStartDate || '') : ''}" ${!canEditGeneral ? 'disabled' : ''}>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>ประเภทพนักงาน *</label>
              <select id="emp-type" required onchange="toggleEmployeeTypeFields(this.value)" ${!canEditGeneral ? 'disabled' : ''}>
                <option value="salary" ${isEdit && emp.type === 'salary' ? 'selected' : ''}>พนักงานเงินเดือนประจำ (Salary)</option>
                <option value="daily" ${isEdit && emp.type === 'daily' ? 'selected' : ''}>พนักงานรายวัน (Daily)</option>
                <option value="freelance" ${isEdit && emp.type === 'freelance' ? 'selected' : ''}>พนักงานฟรีแลนซ์ (Freelancer)</option>
              </select>
            </div>
            <div class="form-group">
              <label id="emp-salary-label">${salaryLabelText}</label>
              <input type="${window.canViewSalary(emp) ? 'number' : 'password'}" id="emp-salaryRate" value="${isEdit ? (window.canViewSalary(emp) ? emp.salaryRate : '0') : ''}" required ${!canEditSalary ? 'disabled' : ''}>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group" style="flex:1;">
              <label>วันที่ปรับฐานเงินเดือนล่าสุด <span style="font-size: 0.8rem; font-weight: 400; color: var(--text-sub);">(ระบบจะอัปเดตให้อัตโนมัติ หรือเลือกกรอกเองสำหรับข้อมูลย้อนหลัง)</span></label>
              <input type="date" id="emp-lastSalaryIncreaseDate" value="${isEdit ? (emp.lastSalaryIncreaseDate || '') : ''}" ${!canEditSalary ? 'disabled' : ''}>
            </div>
          </div>

          <div class="form-row" style="margin-bottom: 20px;">
            <div class="form-group" style="margin-bottom: 0;">
              <div class="switch-container">
                <label class="switch">
                  <input type="checkbox" id="emp-isIntern" ${isEdit && emp.isIntern ? 'checked' : ''} onchange="toggleInternStatus(this.checked)">
                  <span class="slider"></span>
                </label>
                <span>🎓 เป็นพนักงานฝึกงาน (Intern) - จะไม่มีการหักประกันสังคม และจะแนะนำให้หัก ณ ที่จ่ายแทน</span>
              </div>
            </div>
          </div>

          <!-- Withholding Tax & Social Security Toggles -->
          <div style="background: rgba(46, 196, 182, 0.05); border: 1px solid rgba(46, 196, 182, 0.3); border-radius: var(--border-radius-md); padding: 15px; margin-bottom: 20px;">
            <h4 style="font-weight: 700; color: var(--primary-dark); margin-bottom: 15px;">🛡️ การหักภาษี ณ ที่จ่าย & ประกันสังคม</h4>
            
            <div class="form-row" style="grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px;">
              <!-- Withholding Tax Section -->
              <div>
                <div class="switch-container" style="margin-bottom: 10px;">
                  <label class="switch">
                    <input type="checkbox" id="emp-tax-enabled" ${isEdit && dedSettings.withholdingTax.enabled ? 'checked' : (!isEdit ? 'checked' : '')} onchange="document.getElementById('emp-tax-rate-group').style.display = this.checked ? 'block' : 'none'">
                    <span class="slider"></span>
                  </label>
                  <span>หักภาษี ณ ที่จ่าย</span>
                </div>
                <div class="form-group" id="emp-tax-rate-group" style="${isEdit && !dedSettings.withholdingTax.enabled ? 'display: none;' : ''}; margin-bottom: 0;">
                  <label style="font-size: 0.85rem; margin-bottom: 4px;">อัตราภาษีหัก ณ ที่จ่าย</label>
                  <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                    <div style="position: relative; display: inline-block; width: 100px;">
                      <input type="number" id="emp-tax-rate" value="${isEdit && emp.withholdingTax && emp.withholdingTax.isCustom ? emp.withholdingTax.rate : (state.activeCompanySettings.withholdingTaxRate || 3)}" min="0" max="100" step="0.1" style="padding: 6px 20px 6px 10px; width: 100%; border-radius: var(--border-radius-sm); border: 1px solid var(--gray-300); font-weight: 600;" ${isEdit && emp.withholdingTax && !emp.withholdingTax.isCustom ? 'disabled' : (!isEdit ? 'disabled' : '')}>
                      <span style="position: absolute; right: 8px; top: 50%; transform: translateY(-50%); font-weight: 700; color: var(--text-sub); font-size: 0.85rem;">%</span>
                    </div>
                    <label class="custom-checkbox-btn" style="display: inline-flex; align-items: center; gap: 6px; font-weight: 500; font-size: 0.8rem; cursor: pointer; background: var(--gray-200); padding: 6px 12px; border-radius: 6px; border: 1px solid var(--gray-300); transition: all 0.2s; user-select: none; margin: 0; white-space: nowrap;">
                      <input type="checkbox" id="emp-tax-use-default" ${isEdit && emp.withholdingTax && !emp.withholdingTax.isCustom ? 'checked' : (!isEdit ? 'checked' : '')} onchange="toggleCustomTaxRate(this.checked)" style="width: auto; margin: 0; cursor: pointer;">
                      <span>ใช้ค่าเริ่มต้น (${state.activeCompanySettings.withholdingTaxRate || 3}%)</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Social Security Section -->
              <div>
                <div class="switch-container" style="margin-bottom: 10px;">
                  <label class="switch">
                    <input type="checkbox" id="emp-ss-enabled" ${isEdit && dedSettings.socialSecurity.enabled ? 'checked' : (!isEdit ? 'checked' : '')} onchange="document.getElementById('emp-ss-rate-group').style.display = this.checked ? 'block' : 'none'">
                    <span class="slider"></span>
                  </label>
                  <span>หักประกันสังคม</span>
                </div>
                <div class="form-group" id="emp-ss-rate-group" style="${isEdit && !dedSettings.socialSecurity.enabled ? 'display: none;' : ''}; margin-bottom: 0;">
                  <label style="font-size: 0.85rem; margin-bottom: 4px;">อัตราประกันสังคม</label>
                  <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                    <div style="position: relative; display: inline-block; width: 100px;">
                      <input type="number" id="emp-ss-rate" value="${isEdit && emp.socialSecurity && emp.socialSecurity.isCustom ? emp.socialSecurity.rate : (state.activeCompanySettings.socialSecurityRate || 5)}" min="0" max="100" step="0.1" style="padding: 6px 20px 6px 10px; width: 100%; border-radius: var(--border-radius-sm); border: 1px solid var(--gray-300); font-weight: 600;" ${isEdit && emp.socialSecurity && !emp.socialSecurity.isCustom ? 'disabled' : (!isEdit ? 'disabled' : '')}>
                      <span style="position: absolute; right: 8px; top: 50%; transform: translateY(-50%); font-weight: 700; color: var(--text-sub); font-size: 0.85rem;">%</span>
                    </div>
                    <label class="custom-checkbox-btn" style="display: inline-flex; align-items: center; gap: 6px; font-weight: 500; font-size: 0.8rem; cursor: pointer; background: var(--gray-200); padding: 6px 12px; border-radius: 6px; border: 1px solid var(--gray-300); transition: all 0.2s; user-select: none; margin: 0; white-space: nowrap;">
                      <input type="checkbox" id="emp-ss-use-default" ${isEdit && emp.socialSecurity && !emp.socialSecurity.isCustom ? 'checked' : (!isEdit ? 'checked' : '')} onchange="toggleCustomSSRate(this.checked)" style="width: auto; margin: 0; cursor: pointer;">
                      <span>ใช้ค่าเริ่มต้น (${state.activeCompanySettings.socialSecurityRate || 5}%)</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Custom Work Hours Selection (Requirement 28) -->
          <div style="background: rgba(46, 196, 182, 0.05); border: 1px solid rgba(46, 196, 182, 0.3); border-radius: var(--border-radius-md); padding: 15px; margin-bottom: 20px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
              <h4 style="font-weight: 700; color: var(--primary-dark); margin: 0;">⏱️ เวลาทำงานและวันหยุดประจำสัปดาห์</h4>
            </div>
            
            <div class="switch-container" style="margin-bottom: 15px;">
              <label class="switch">
                <input type="checkbox" id="emp-use-custom-work-hours" ${isEdit && emp.useCustomWorkHours ? 'checked' : ''} onchange="document.getElementById('emp-custom-work-hours-container').style.display = this.checked ? 'block' : 'none'">
                <span class="slider"></span>
              </label>
              <span style="font-weight: 500;">กำหนดเวลาทำงานและวันหยุดเฉพาะพนักงานคนนี้ (ไม่ใช้ค่าตั้งต้นบริษัท)</span>
            </div>

            <div id="emp-custom-work-hours-container" style="display: ${isEdit && emp.useCustomWorkHours ? 'block' : 'none'}; border-top: 1px dashed var(--gray-300); padding-top: 15px;">
              <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 10px;">
                ${['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
    const dayNamesTh = { monday: 'วันจันทร์', tuesday: 'วันอังคาร', wednesday: 'วันพุธ', thursday: 'วันพฤหัสบดี', friday: 'วันศุกร์', saturday: 'วันเสาร์', sunday: 'วันอาทิตย์' };

    let startHr = '08', startMin = '00', endHr = '17', endMin = '00', active = true;

    if (isEdit && emp.customWorkHours && emp.customWorkHours[day]) {
      let str = emp.customWorkHours[day];
      if (str.startsWith('OFF:')) {
        active = false;
        str = str.substring(4);
      }
      const parts = str.split('-');
      if (parts.length === 2) {
        const s = parts[0].split(':');
        if (s.length === 2) { startHr = s[0].padStart(2, '0'); startMin = s[1].padStart(2, '0'); }
        const e = parts[1].split(':');
        if (e.length === 2) { endHr = e[0].padStart(2, '0'); endMin = e[1].padStart(2, '0'); }
      }
    } else {
      let str = state.activeCompanySettings?.workHoursPerDay?.[day] || '08:00-17:00';
      if (str === '0' || str.startsWith('OFF:')) {
        active = false;
        if (str.startsWith('OFF:')) str = str.substring(4);
      }
      if (str !== '0') {
        const parts = str.split('-');
        if (parts.length === 2) {
          const s = parts[0].split(':');
          if (s.length === 2) { startHr = s[0].padStart(2, '0'); startMin = s[1].padStart(2, '0'); }
          const e = parts[1].split(':');
          if (e.length === 2) { endHr = e[0].padStart(2, '0'); endMin = e[1].padStart(2, '0'); }
        }
      }
    }

    const hrOpts = (selected) => Array.from({ length: 24 }, (_, i) => {
      const v = String(i).padStart(2, '0');
      return `<option value="${v}" ${v === selected ? 'selected' : ''}>${v}</option>`;
    }).join('');

    const minOpts = (selected) => Array.from({ length: 60 }, (_, i) => {
      const v = String(i).padStart(2, '0');
      return `<option value="${v}" ${v === selected ? 'selected' : ''}>${v}</option>`;
    }).join('');

    return `
                    <div style="background:var(--gray-200); border:1px solid var(--gray-300); padding:10px; border-radius:var(--border-radius-md); display:flex; flex-direction:column; gap:8px;">
                      <div style="display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid var(--gray-300); padding-bottom:6px; margin-bottom:4px;">
                        <strong style="color:var(--primary-dark); font-size:0.85rem;">${dayNamesTh[day]} <span id="emp-dayoff-label-${day}" style="color:var(--text-sub); font-size:0.75rem; font-weight:normal;">${active ? '' : ' (วันหยุด)'}</span></strong>
                        <label class="switch switch-sm" style="transform:scale(0.85); transform-origin:right;">
                          <input type="checkbox" id="emp-workday-${day}" onchange="document.getElementById('emp-dayoff-label-${day}').textContent = this.checked ? '' : ' (วันหยุด)';" ${active ? 'checked' : ''}>
                          <span class="slider"></span>
                        </label>
                      </div>
                      <div style="display:flex; flex-direction:column; gap:6px;">
                        <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                          <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เริ่มงาน</span>
                          <div style="display:flex; align-items:center; gap:2px;">
                            <select id="emp-start-hr-${day}" style="padding:4px; font-size:0.8rem;">${hrOpts(startHr)}</select>
                            <span style="font-weight:bold; font-size:0.8rem;">:</span>
                            <select id="emp-start-min-${day}" style="padding:4px; font-size:0.8rem;">${minOpts(startMin)}</select>
                          </div>
                        </div>
                        <div style="display:flex; align-items:center; gap:4px; justify-content:space-between;">
                          <span style="font-size:0.75rem; color:var(--text-sub); min-width:35px;">เลิกงาน</span>
                          <div style="display:flex; align-items:center; gap:2px;">
                            <select id="emp-end-hr-${day}" style="padding:4px; font-size:0.8rem;">${hrOpts(endHr)}</select>
                            <span style="font-weight:bold; font-size:0.8rem;">:</span>
                            <select id="emp-end-min-${day}" style="padding:4px; font-size:0.8rem;">${minOpts(endMin)}</select>
                          </div>
                        </div>
                      </div>
                    </div>
                  `;
  }).join('')}
              </div>
            </div>
          </div>

          <!-- Security Deposit (Requirement 5) -->
          <div style="background: rgba(255, 183, 3, 0.05); border: 1px solid rgba(255, 183, 3, 0.3); border-radius: var(--border-radius-md); padding: 15px; margin-bottom: 20px;">
            <div class="switch-container" style="margin-bottom: 10px;">
              <label class="switch">
                <input type="checkbox" id="emp-requiresSecurityDeposit" ${isEdit && emp.requiresSecurityDeposit ? 'checked' : ''} onchange="toggleSecurityDepositFields(this.checked)">
                <span class="slider"></span>
              </label>
              <span>🔒 มีระบบหักเงินประกันเก็บเอาไว้ (หักงวดแรกครั้งเดียวเต็มจำนวน คืนเมื่อลาออก)</span>
            </div>
            <div class="form-row" id="security-deposit-fields" style="${isEdit && emp.requiresSecurityDeposit ? '' : 'display: none;'}">
              <div class="form-group">
                <label>จำนวนเงินประกันที่ต้องหัก (บาท)</label>
                <input type="number" id="emp-securityDepositAmount" value="${isEdit ? (emp.securityDepositAmount || 0) : 0}">
              </div>
              <div class="form-group">
                <label>สถานะเงินประกันปัจจุบัน</label>
                <select id="emp-securityDepositStatus">
                  <option value="none" ${isEdit && emp.securityDepositStatus === 'none' ? 'selected' : ''}>ยังไม่หัก</option>
                  <option value="deducted" ${isEdit && emp.securityDepositStatus === 'deducted' ? 'selected' : ''}>หักเอาไว้แล้ว</option>
                  <option value="refunded" ${isEdit && emp.securityDepositStatus === 'refunded' ? 'selected' : ''}>คืนพนักงานแล้ว</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Student Loan Fund (กยศ) (Requirement 9) -->
          <div style="background: rgba(0, 180, 216, 0.05); border: 1px solid rgba(0, 180, 216, 0.3); border-radius: var(--border-radius-md); padding: 15px; margin-bottom: 20px;">
            <div class="switch-container" style="margin-bottom: 10px;">
              <label class="switch">
                <input type="checkbox" id="emp-loanEnabled" ${isEdit && emp.studentLoanFund?.enabled ? 'checked' : ''} onchange="toggleLoanFields(this.checked)">
                <span class="slider"></span>
              </label>
              <span>🎓 มีรายการหักเงินนำส่ง กยศ. (ปรับยอดหักได้ทุกๆ เดือน)</span>
            </div>
            <div class="form-row" id="loan-fields" style="${isEdit && emp.studentLoanFund?.enabled ? '' : 'display: none;'}">
              <div class="form-group">
                <label>จำนวนเงินที่ต้องหักต่อเดือน (บาท)</label>
                <input type="number" id="emp-loanAmount" value="${isEdit && emp.studentLoanFund ? emp.studentLoanFund.monthlyAmount : 0}">
              </div>
            </div>
          </div>

          <!-- Diligence Allowance Setup (เบี้ยขยันรายบุคคล) -->
          <div style="background: rgba(131, 56, 236, 0.05); border: 1px solid rgba(131, 56, 236, 0.3); border-radius: var(--border-radius-md); padding: 15px; margin-bottom: 20px; ${state.activeCompanySettings.enableDiligenceAllowance !== false ? '' : 'display: none;'}">
            <div class="switch-container" style="margin-bottom: 10px;">
              <label class="switch">
                <input type="checkbox" id="emp-diligence-enabled" ${isEdit && emp.diligenceAllowance?.enabled !== false ? 'checked' : (!isEdit ? 'checked' : '')} onchange="toggleDiligenceFields(this.checked)">
                <span class="slider"></span>
              </label>
              <span>🏆 มีสิทธิ์ได้รับเบี้ยขยันประจำเดือน</span>
            </div>
            <div class="form-row" id="diligence-fields" style="${isEdit && emp.diligenceAllowance?.enabled === false ? 'display: none;' : ''}">
              <div class="form-group" style="margin-bottom: 0;">
                <label style="font-size: 0.85rem; margin-bottom: 4px;">จำนวนเงินเบี้ยขยันประจำเดือน</label>
                <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                  <div style="position: relative; display: inline-block; width: 150px;">
                    <input type="number" id="emp-diligence-amount" value="${isEdit && emp.diligenceAllowance && emp.diligenceAllowance.isCustom ? emp.diligenceAllowance.amount : (state.activeCompanySettings.diligenceAllowanceAmount !== undefined ? state.activeCompanySettings.diligenceAllowanceAmount : 1000)}" min="0" style="padding: 6px 10px; width: 100%; border-radius: var(--border-radius-sm); border: 1px solid var(--gray-300); font-weight: 600;" ${isEdit && emp.diligenceAllowance && !emp.diligenceAllowance.isCustom ? 'disabled' : (!isEdit ? 'disabled' : '')}>
                  </div>
                  <label class="custom-checkbox-btn" style="display: inline-flex; align-items: center; gap: 6px; font-weight: 500; font-size: 0.8rem; cursor: pointer; background: var(--gray-200); padding: 6px 12px; border-radius: 6px; border: 1px solid var(--gray-300); transition: all 0.2s; user-select: none; margin: 0; white-space: nowrap;">
                    <input type="checkbox" id="emp-diligence-use-default" ${isEdit && emp.diligenceAllowance && !emp.diligenceAllowance.isCustom ? 'checked' : (!isEdit ? 'checked' : '')} onchange="toggleCustomDiligenceAmount(this.checked)" style="width: auto; margin: 0; cursor: pointer;">
                    <span>ใช้ค่าเริ่มต้น (${state.activeCompanySettings.diligenceAllowanceAmount !== undefined ? state.activeCompanySettings.diligenceAllowanceAmount : 1000} บาท)</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <!-- Login Accounts Setup (Requirement 17 & 18) -->
          <div style="border-top: 1px solid var(--gray-300); padding-top: 15px;">
            <h4>🔐 ตั้งค่าบัญชีเข้าระบบ (Login Account)</h4>
            
            <div class="switch-container" style="margin-top: 15px; margin-bottom: 15px;">
              <label class="switch">
                <input type="checkbox" id="emp-login-enabled" ${isEdit && emp.loginAccount?.enabled !== false ? 'checked' : ''} onchange="toggleLoginAccountFields(this.checked)">
                <span class="slider"></span>
              </label>
              <span>เปิดใช้งานสิทธิ์การล็อกอินเข้าระบบสำหรับคนนี้</span>
            </div>

            <div id="login-account-fields" style="${(!isEdit || (isEdit && emp.loginAccount?.enabled === false)) ? 'display: none;' : ''}">
              <div class="form-row" style="grid-template-columns: 1fr 2fr;">
                <div class="form-group">
                  <label>สิทธิ์เข้าใช้งานระบบ (Permissions Role) *</label>
                  <select id="emp-role" required ${!canAssignRole ? 'disabled title="สิทธิ์นี้สงวนไว้สำหรับ Superadmin เท่านั้น"' : ''}>
                    <option value="member" ${isEdit && (emp.role === 'member' || emp.role === 'employee' || emp.role === 'viewer_a') ? 'selected' : (!isEdit ? 'selected' : '')}>Member (พนักงานทั่วไป)</option>
                    <option value="admin" ${isEdit && (emp.role === 'admin' || emp.role === 'manager_a') ? 'selected' : ''}>Admin (ผู้จัดการ/แอดมิน)</option>
                    <option value="superadmin" ${isEdit && emp.role === 'superadmin' ? 'selected' : ''}>Superadmin (ผู้ดูแลระบบสูงสุด)</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>อีเมลสำหรับเข้าใช้ (Email) *</label>
                  <input type="email" id="emp-email" value="${isEdit ? escapeAttr(emp.email || '') : ''}" placeholder="example@gmail.com" ${!canAssignRole && !canEditGeneral ? 'disabled' : ''}>
                </div>
              </div>
              <div class="form-row" style="margin-top: 10px;">
                <div class="form-group">
                  <label>ตั้งวันปิดการเข้าใช้งานล่วงหน้า (ระงับสิทธิ์อัตโนมัติเมื่อถึงกำหนด - ไม่บังคับ)</label>
                  <input type="date" id="emp-login-deactivation-date" value="${isEdit && emp.loginAccount?.deactivationDate ? emp.loginAccount.deactivationDate : ''}">
                </div>
              </div>
            </div>
          </div>


        </form>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" onclick="closeEmployeeModal()">ยกเลิก</button>
        <button class="btn btn-primary" onclick="submitEmployeeForm('${isEdit ? escapeJsParam(emp.id) : ''}')">บันทึกข้อมูล</button>
      </div>
    </div>
  `;

  modalOverlay.classList.add('active');

  // Prevent form enter key submit page refreshes
  const empForm = document.getElementById('employee-detail-form');
  if (empForm) {
    empForm.addEventListener('submit', (e) => {
      e.preventDefault();
      submitEmployeeForm(isEdit ? emp.id : '');
    });
  }
}

function toggleEmployeeTypeFields(val) {
  const label = document.getElementById('emp-salary-label');
  if (val === 'salary') {
    label.textContent = 'อัตราจ้าง (เงินเดือนประจำ) *';
  } else if (val === 'daily') {
    label.textContent = 'อัตราจ้าง (รายวัน) *';
  } else {
    label.textContent = 'อัตราจ้าง (เหมาจ่ายรายวัน) *';
  }
};

function toggleSecurityDepositFields(checked) {
  document.getElementById('security-deposit-fields').style.display = checked ? 'grid' : 'none';
};

function toggleLoanFields(checked) {
  document.getElementById('loan-fields').style.display = checked ? 'grid' : 'none';
};

function toggleEmployeeStatusFields(status) {
  const isResigned = status === 'resigned';
  const box = document.getElementById('resignation-details-box');
  if (box) box.style.display = isResigned ? 'block' : 'none';
  const resignInput = document.getElementById('emp-resignDate');
  if (isResigned && resignInput && !resignInput.value) {
    resignInput.value = new Date().toISOString().split('T')[0];
  }
  const refundGroup = document.getElementById('resign-refund-group');
  const requiresDeposit = document.getElementById('emp-requiresSecurityDeposit')?.checked;
  if (refundGroup) {
    refundGroup.style.display = (isResigned && requiresDeposit) ? 'block' : 'none';
    if (isResigned) {
      const refundInput = document.getElementById('emp-securityDepositRefundAmount');
      const depositInput = document.getElementById('emp-securityDepositAmount');
      if (refundInput && depositInput && (!refundInput.value || refundInput.value == '0' || refundInput.value == '0.00')) {
        refundInput.value = depositInput.value || 0;
      }
    }
  }
}

function toggleResignFields(checked) {
  const statusSelect = document.getElementById('emp-status');
  if (statusSelect) {
    statusSelect.value = checked ? 'resigned' : 'active';
  }
  toggleEmployeeStatusFields(checked ? 'resigned' : 'active');
};

function toggleLoginAccountFields(checked) {
  document.getElementById('login-account-fields').style.display = checked ? 'block' : 'none';
};

function addNewRecurringRow() {
  const container = document.getElementById('recurring-items-list');
  const div = document.createElement('div');
  div.className = 'recurring-item-row';
  div.style.marginBottom = '10px';
  div.innerHTML = `
    <input type="text" placeholder="รายการ (เช่น ค่าเดินทาง)" class="rec-name" required style="padding: 8px 12px;">
    <select class="rec-type" style="padding: 8px 12px; width: 100px;">
      <option value="income">รายรับ</option>
      <option value="deduction">รายหัก</option>
    </select>
    <input type="number" placeholder="จำนวนเงิน" class="rec-amount" required style="padding: 8px 12px; width: 120px;">
    <button type="button" class="btn btn-danger btn-sm" onclick="this.parentElement.remove()" style="padding: 8px;">❌</button>
  `;
  container.appendChild(div);
};

function addTimesheetAdjustmentRow() {
  const container = document.getElementById('timesheet-adjustments-list');
  const div = document.createElement('div');
  div.className = 'adj-item-row';
  div.style.marginBottom = '12px';
  div.style.padding = '10px';
  div.style.border = '1px solid var(--gray-300)';
  div.style.borderRadius = '6px';
  div.style.background = '#fcfcfc';
  div.style.display = 'flex';
  div.style.flexDirection = 'column';
  div.style.gap = '8px';
  div.innerHTML = `
    <div style="display: flex; gap: 8px;">
      <input type="text" placeholder="รายการ" class="adj-name" required style="padding: 8px; flex: 2; font-size: 0.9rem;">
      <select class="adj-type" style="padding: 8px; flex: 1; font-size: 0.9rem;">
        <option value="income">รายรับ</option>
        <option value="deduction">รายหัก</option>
      </select>
    </div>
    <div style="display: flex; gap: 8px; align-items: center;">
      <input type="number" placeholder="จำนวน" class="adj-amount" required style="padding: 8px; flex: 1; font-size: 0.9rem;">
      <select class="adj-recurrence" style="padding: 8px; flex: 2; font-size: 0.85rem; background: var(--info-light);">
        <option value="onetime" selected>📌 เฉพาะเดือนนี้</option>
        <option value="recurring">🔄 ประจำทุกเดือน</option>
      </select>
      <button type="button" class="btn btn-danger btn-sm" onclick="this.closest('.adj-item-row').remove()" style="padding: 8px; font-weight: bold;">❌</button>
    </div>
  `;
  container.appendChild(div);
};

function closeEmployeeModal() {
  document.getElementById('employee-modal-overlay').classList.remove('active');
};

async function submitEmployeeForm(existingId = '') {
  const form = document.getElementById('employee-detail-form');
  if (!form.reportValidity()) return;

  const isLoginEnabled = document.getElementById('emp-login-enabled').checked;
  const emailInputVal = document.getElementById('emp-email').value.trim();
  if (isLoginEnabled && !emailInputVal) {
    alert("กรุณากรอกอีเมลสำหรับใช้เข้าสู่ระบบ");
    return;
  }

  // Retrieve custom work hours
  const useCustomWorkHours = document.getElementById('emp-use-custom-work-hours')?.checked || false;
  const customWorkHours = {};
  const weeklyOff = [];

  if (useCustomWorkHours) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    days.forEach((dayEn, idx) => {
      const isWorkday = document.getElementById(`emp-workday-${dayEn}`)?.checked;
      const startHr = document.getElementById(`emp-start-hr-${dayEn}`)?.value || '08';
      const startMin = document.getElementById(`emp-start-min-${dayEn}`)?.value || '00';
      const endHr = document.getElementById(`emp-end-hr-${dayEn}`)?.value || '17';
      const endMin = document.getElementById(`emp-end-min-${dayEn}`)?.value || '00';
      const timeStr = `${startHr}:${startMin}-${endHr}:${endMin}`;

      customWorkHours[dayEn] = isWorkday ? timeStr : `OFF:${timeStr}`;
      if (!isWorkday) {
        weeklyOff.push(idx);
      }
    });
  } else {
    // If not using custom, sync weeklyOff with company settings to maintain backward compatibility
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    days.forEach((dayEn, idx) => {
      const workHoursStr = state.activeCompanySettings?.workHoursPerDay?.[dayEn] || '8';
      if (workHoursStr === '0' || workHoursStr.startsWith('OFF:')) {
        weeklyOff.push(idx);
      }
    });
  }
  // Preserve existing recurring items from profile
  const empToEdit = existingId ? state.employees.find(e => e.id === existingId) : null;
  const recurringItems = empToEdit ? (empToEdit.recurringItems || []) : [];

  const payload = {
    companyId: state.activeCompanyId,
    realName: document.getElementById('emp-realName').value.trim(),
    nickname: document.getElementById('emp-nickname').value.trim(),
    position: document.getElementById('emp-position').value.trim(),
    department: document.getElementById('emp-department').value.trim() || '-',
    citizenId: document.getElementById('emp-citizenId').value.trim(),
    bankName: document.getElementById('emp-bankName') ? document.getElementById('emp-bankName').value.trim() : '',
    bankAccount: document.getElementById('emp-bankAccount').value.trim(),
    startDate: document.getElementById('emp-startDate').value,
    resignDate: document.getElementById('emp-resignDate').value || null,
    socialSecurityStartDate: document.getElementById('emp-socialSecurityStartDate').value || null,
    type: document.getElementById('emp-type').value,
    salaryRate: (() => {
      const inputVal = document.getElementById('emp-salaryRate').value;
      if (empToEdit && !window.canViewSalary(empToEdit) && (!inputVal || inputVal === '0' || inputVal === '')) {
        return empToEdit.salaryRate || 0; // preserve original salary if masked
      }
      return parseFloat(inputVal || 0);
    })(),
    lastSalaryIncreaseDate: document.getElementById('emp-lastSalaryIncreaseDate')?.value || null,
    isIntern: document.getElementById('emp-isIntern') ? document.getElementById('emp-isIntern').checked : false,
    withholdingTax: {
      enabled: document.getElementById('emp-tax-enabled').checked,
      rate: parseFloat(document.getElementById('emp-tax-rate').value || 0),
      isCustom: !document.getElementById('emp-tax-use-default').checked
    },
    socialSecurity: {
      enabled: document.getElementById('emp-ss-enabled').checked,
      rate: parseFloat(document.getElementById('emp-ss-rate').value || 0),
      isCustom: !document.getElementById('emp-ss-use-default').checked
    },
    weeklyDaysOff: weeklyOff.length === 0 ? [-1] : weeklyOff,
    useCustomWorkHours: useCustomWorkHours,
    customWorkHours: customWorkHours,
    requiresSecurityDeposit: document.getElementById('emp-requiresSecurityDeposit').checked,
    securityDepositAmount: parseFloat(document.getElementById('emp-securityDepositAmount')?.value || 0),
    securityDepositStatus: document.getElementById('emp-securityDepositStatus')?.value || 'none',
    securityDepositRefundAmount: document.getElementById('emp-securityDepositRefundAmount') ? parseFloat(document.getElementById('emp-securityDepositRefundAmount').value || 0) : 0,
    studentLoanFund: {
      enabled: document.getElementById('emp-loanEnabled').checked,
      monthlyAmount: parseFloat(document.getElementById('emp-loanAmount')?.value || 0)
    },
    diligenceAllowance: {
      enabled: document.getElementById('emp-diligence-enabled').checked,
      amount: parseFloat(document.getElementById('emp-diligence-amount').value || 0),
      isCustom: !document.getElementById('emp-diligence-use-default').checked
    },
    recurringItems,
    loginAccount: {
      enabled: isLoginEnabled,
      deactivationDate: document.getElementById('emp-login-deactivation-date').value || null
    },
    role: document.getElementById('emp-role').value,
    email: emailInputVal.toLowerCase() || null,
    username: emailInputVal.toLowerCase() || null
  };

  // Auto-detect salary increase
  if (empToEdit && payload.salaryRate > (empToEdit.salaryRate || 0)) {
    payload.lastSalaryIncreaseDate = new Date().toISOString().split('T')[0];
  }

  const statusSelect = document.getElementById('emp-status');
  const empStatus = statusSelect ? statusSelect.value : (empToEdit?.status || 'active');
  const resignInput = document.getElementById('emp-resignDate');
  const lastWorkingDay = (empStatus === 'resigned' && resignInput) ? (resignInput.value || null) : null;

  payload.status = empStatus;
  payload.resignDate = lastWorkingDay;
  payload.resignedDate = lastWorkingDay;

  const refundInput = document.getElementById('emp-securityDepositRefundAmount');
  if (refundInput && empStatus === 'resigned') {
    payload.securityDepositRefundAmount = parseFloat(refundInput.value || 0);
  }

  try {
    if (existingId) {
      payload.id = existingId;
      const existingEmp = state.employees.find(e => e.id === existingId);
      if (existingEmp && existingEmp.sortOrder !== undefined) {
        payload.sortOrder = existingEmp.sortOrder;
      }
      await updateEmployee(existingId, payload);

      // Keep currentUser in sync if editing self (Fixes the F5 revert issue)
      if (state.currentUser && state.currentUser.id === existingId) {
        state.currentUser = { ...state.currentUser, ...payload };
        localStorage.setItem('wuru_user', JSON.stringify(state.currentUser));

        // Update nav bar immediately
        const profName = document.getElementById('profile-name');
        if (profName) profName.textContent = state.currentUser.nickname || (state.currentUser.realName ? state.currentUser.realName.split(' ')[0] : 'ผู้ใช้งาน');
      }
    } else {
      payload.sortOrder = state.employees.length;
      await addEmployee(payload);
    }
    closeEmployeeModal();
    navigateTo('employees');
    toast.success(existingId ? "บันทึกการแก้ไขข้อมูลพนักงานเรียบร้อยแล้ว" : "เพิ่มพนักงานใหม่เข้าสู่ระบบสำเร็จ!");
  } catch (err) {
    console.error(err);
    toast.error("เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง");
  }
};

// Resigned employees archive dialog (Requirement 23)
async function showResignedArchiveModal() {
  let modalOverlay = document.getElementById('resigned-modal-overlay');
  if (!modalOverlay) {
    modalOverlay = document.createElement('div');
    modalOverlay.id = 'resigned-modal-overlay';
    modalOverlay.className = 'modal-overlay';
    document.body.appendChild(modalOverlay);
  }

  modalOverlay.innerHTML = `
    <div class="modal-container">
      <div class="modal-header">
        <h3>พนักงานที่ลาออกไปแล้วครบ 2 ปีขึ้นไป</h3>
        <button class="btn-close-modal" onclick="document.getElementById('resigned-modal-overlay').classList.remove('active')">&times;</button>
      </div>
      <div class="modal-body">
        <p style="color: var(--text-sub); margin-bottom: 20px;">รายชื่อพนักงานที่ลาออกแล้วเป็นเวลามากกว่า 2 ปี ซึ่งตรงตามเกณฑ์ของระบบความปลอดภัยข้อมูลที่เปิดให้แอดมินสามารถพิจารณาลบข้อมูลพนักงานออกได้อย่างถาวร</p>
        <div class="table-responsive">
          <table class="payroll-table">
            <thead>
              <tr>
                <th>ลูกน้อง</th>
                <th>วันที่ลาออก</th>
                <th>ระยะเวลาลาออก</th>
                <th>การจัดการ</th>
              </tr>
            </thead>
            <tbody id="resigned-archive-rows">
              <tr><td colspan="4" class="text-center">กำลังดึงข้อมูล...</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;

  modalOverlay.classList.add('active');

  // Load resigned employees >= 2 years
  const rowsBody = document.getElementById('resigned-archive-rows');
  try {
    const archiveList = await getResignedArchiveEmployees(state.activeCompanyId);
    if (archiveList.length === 0) {
      rowsBody.innerHTML = `<tr><td colspan="4" class="text-center" style="color: var(--text-sub);">ไม่พบพนักงานลาออกที่อยู่ในเกณฑ์ครบ 2 ปีในขณะนี้</td></tr>`;
      return;
    }

    let rowsHTML = '';
    archiveList.forEach(emp => {
      const msElapsed = Date.now() - new Date(emp.resignedDate).getTime();
      const yearsElapsed = (msElapsed / (365 * 24 * 60 * 60 * 1000)).toFixed(1);

      rowsHTML += `
        <tr>
          <td><strong>${escapeHtml(emp.realName)}</strong> (${escapeHtml(emp.nickname)})</td>
          <td>${emp.resignedDate}</td>
          <td><span style="color: var(--danger); font-weight:700;">${yearsElapsed} ปี</span></td>
          <td>
            ${window.checkPermission('delete_employee') ? `<button class="btn btn-danger btn-sm" onclick="triggerHardDeleteEmployee('${escapeJsParam(emp.id)}')">🗑️ ลบถาวร</button>` : '🔒 ไม่มีสิทธิ์'}
          </td>
        </tr>
      `;
    });
    rowsBody.innerHTML = rowsHTML;
  } catch (err) {
    console.error(err);
    rowsBody.innerHTML = `<tr><td colspan="4" class="text-center">เกิดข้อผิดพลาดในการโหลด</td></tr>`;
  }
};

async function triggerHardDeleteEmployee(empId) {
  if (!window.checkPermission('delete_employee')) {
    toast.warning("ขออภัย! คุณไม่มีสิทธิ์ลบพนักงานออกจากระบบ");
    return;
  }
  const confirmed = await showConfirm({
    title: 'ยืนยันการลบพนักงานถาวร',
    message: 'คุณต้องการลบข้อมูลของพนักงานคนนี้ออกจากระบบอย่างถาวรจริงหรือไม่? การลบนี้ไม่สามารถกู้คืนข้อมูลได้!',
    confirmText: '🗑️ ลบถาวร',
    isDanger: true
  });
  if (!confirmed) return;
  try {
    await deleteEmployee(empId);
    toast.success("ลบข้อมูลพนักงานเรียบร้อยแล้ว");
    document.getElementById('resigned-modal-overlay')?.classList.remove('active');
    navigateTo('employees');
  } catch (err) {
    console.error(err);
    toast.error("ไม่สามารถลบข้อมูลได้: " + (err.message || ''));
  }
};

// Detailed view modal for employee records and documents upload (Requirement 2, 29, 30)
async function viewEmployeeDetails(empId) {
  const emp = state.employees.find(e => e.id === empId);

  let modalOverlay = document.getElementById('details-modal-overlay');
  if (!modalOverlay) {
    modalOverlay = document.createElement('div');
    modalOverlay.id = 'details-modal-overlay';
    modalOverlay.className = 'modal-overlay';
    document.body.appendChild(modalOverlay);
  }

  let attachmentsHTML = '';
  if (emp.attachments && emp.attachments.length > 0) {
    emp.attachments.forEach((attach, idx) => {
      attachmentsHTML += `
        <div style="display: flex; justify-content: space-between; align-items: center; background: var(--gray-200); padding: 10px 15px; border-radius: var(--border-radius-sm); margin-bottom: 8px;">
          <span>📄 <strong>${escapeHtml(attach.name)}</strong> (อัปโหลดเมื่อ: ${escapeHtml(attach.uploadedAt)})</span>
          <div style="display: flex; align-items: center; gap: 8px;">
            <!-- Bubble Download Menu -->
            <div class="bubble-menu-container">
              <button class="btn btn-secondary btn-sm bubble-main-btn" onclick="toggleBubbleMenu(this)" style="padding: 4px 10px;">📥 ดาวน์โหลด</button>
              <div class="bubble-options">
                <button class="bubble-btn bubble-webp" onclick="downloadDocumentAs('${escapeJsParam(attach.url)}', '${escapeJsParam(attach.name)}', 'webp')" title="ดาวน์โหลดเป็น WebP (ต้นฉบับ)">WebP</button>
                <button class="bubble-btn bubble-jpg" onclick="downloadDocumentAs('${escapeJsParam(attach.url)}', '${escapeJsParam(attach.name)}', 'jpg')" title="แปลงและดาวน์โหลดเป็น JPG">JPG</button>
                <button class="bubble-btn bubble-pdf" onclick="downloadDocumentAs('${escapeJsParam(attach.url)}', '${escapeJsParam(attach.name)}', 'pdf')" title="แปลงและดาวน์โหลดเป็น PDF">PDF</button>
              </div>
            </div>
            ${window.checkPermission('manage_employee_files') ? `<button class="btn btn-danger btn-sm" onclick="removeEmployeeAttachment('${escapeJsParam(emp.id)}', ${idx})" style="padding: 4px 10px;">ลบ</button>` : ''}
          </div>
        </div>
      `;
    });
  } else {
    attachmentsHTML = `<p style="color: var(--text-sub);">ยังไม่มีการอัปโหลดไฟล์เอกสาร</p>`;
  }

  let disciplinaryHTML = '';
  let hasActiveWarning = false;
  if (emp.disciplinaryRecords && emp.disciplinaryRecords.length > 0) {
    // Sort descending by date
    const sortedRecords = [...emp.disciplinaryRecords].sort((a, b) => new Date(b.issueDate) - new Date(a.issueDate));
    sortedRecords.forEach((record) => {
      const isWarning = record.type === 'warning';
      const typeLabel = isWarning ? '<span class="badge badge-warning">หนังสือเตือน</span>' : '<span class="badge badge-danger">เลิกจ้าง</span>';

      // Check active warning (within 1 year)
      if (isWarning) {
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        if (new Date(record.issueDate) >= oneYearAgo) {
          hasActiveWarning = true;
        }
      }

      disciplinaryHTML += `
        <div style="padding: 10px; border: 1px solid var(--gray-300); border-radius: 8px; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center; background: white;">
          <div>
            ${typeLabel} <strong>วันที่:</strong> ${record.issueDate} <br>
            <span style="font-size: 0.85rem; color: var(--text-sub);"><strong>สาเหตุ:</strong> ${record.reason}</span>
          </div>
          <div style="display: flex; gap: 5px;">
            <button class="btn btn-secondary btn-sm" onclick="printDisciplinaryLetter('${emp.id}', '${record.id}')">🖨️ พิมพ์</button>
            ${window.checkPermission('manage_disciplinary') ? `<button class="btn btn-danger btn-sm" onclick="deleteDisciplinaryRecord('${emp.id}', '${record.id}')">❌ ลบ</button>` : ''}
          </div>
        </div>
      `;
    });
  } else {
    disciplinaryHTML = `<p style="color: var(--text-sub); font-size: 0.9rem;">ประวัติขาวสะอาด (ไม่มีการออกหนังสือเตือน)</p>`;
  }

  let tenureText = '-';
  if (emp.startDate) {
    const start = new Date(emp.startDate);
    const now = new Date();
    let years = now.getFullYear() - start.getFullYear();
    let months = now.getMonth() - start.getMonth();
    let days = now.getDate() - start.getDate();
    if (days < 0) {
      months--;
      const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      days += lastMonth.getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }
    let parts = [];
    if (years > 0) parts.push(`${years} ปี`);
    if (months > 0) parts.push(`${months} เดือน`);
    if (days > 0) parts.push(`${days} วัน`);
    tenureText = parts.length > 0 ? parts.join(' ') : 'เริ่มงานวันนี้';
  }

  const tenureHTML = window.checkPermission('view_employee_tenure')
    ? `<div style="color: var(--info);">⏳ <strong>อายุงาน:</strong> ${tenureText}</div>` : '';
  const salaryIncreaseHTML = window.checkPermission('view_employee_salary_increase')
    ? `<div style="color: var(--success);">📈 <strong>ปรับฐานเงินเดือนล่าสุด:</strong> ${emp.lastSalaryIncreaseDate || '-'}</div>` : '';

  modalOverlay.innerHTML = `
    <div class="modal-container" style="max-width: 750px;">
      <div class="modal-header">
        <h3>แฟ้มข้อมูลพนักงาน: ${escapeHtml(emp.realName)} (${escapeHtml(emp.nickname)})</h3>
        <button class="btn-close-modal" onclick="document.getElementById('details-modal-overlay').classList.remove('active')">&times;</button>
      </div>
      <div class="modal-body" style="display: flex; flex-direction: column; gap: 20px;">
        <div class="glass-card" style="background: var(--gray-200);">
          <h4 style="font-weight: 700; color: var(--primary-dark); margin-bottom: 15px;">ข้อมูลประวัติส่วนบุคคล</h4>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 0.9rem;">
            <div>👤 <strong>ชื่อจริง:</strong> ${escapeHtml(emp.realName)}</div>
            <div>👶 <strong>ชื่อเล่น:</strong> ${escapeHtml(emp.nickname)}</div>
            <div>🆔 <strong>เลขบัตรประชาชน:</strong> ${escapeHtml(emp.citizenId)}</div>
            <div>🏦 <strong>บัญชีธนาคาร:</strong> ${emp.bankAccount ? `${emp.bankName ? `[${escapeHtml(emp.bankName)}] ` : ''}${escapeHtml(emp.bankAccount)}` : 'รับเงินสด'}</div>
            <div>📅 <strong>วันที่เริ่มทำงาน:</strong> ${escapeHtml(emp.startDate)}</div>
            <div>🚦 <strong>สถานะการทำงาน:</strong> ${emp.status === 'resigned' ? `<span class="badge badge-danger">ลาออกแล้ว</span> ${emp.resignDate || emp.resignedDate ? `(วันสุดท้าย: ${escapeHtml(emp.resignDate || emp.resignedDate)})` : ''}` : `<span class="badge badge-success">กำลังทำงาน</span>`}</div>
            ${tenureHTML}
            <div>🛡️ <strong>เริ่มประกันสังคม:</strong> ${escapeHtml(emp.socialSecurityStartDate || 'ไม่มีประกันสังคม')}</div>
            ${salaryIncreaseHTML}
            ${emp.studentLoanFund?.enabled ? `<div style="color: var(--danger);">🎓 <strong>หัก กยศ:</strong> ${emp.studentLoanFund.monthlyAmount} บาท/เดือน</div>` : '<div>🎓 <strong>หัก กยศ:</strong> ไม่ได้หัก</div>'}
            ${emp.requiresSecurityDeposit ? `<div style="color: var(--warning);">🔒 <strong>เงินประกัน:</strong> ${emp.securityDepositAmount} บาท (${emp.securityDepositStatus === 'deducted' ? 'หักแล้ว' : (emp.securityDepositStatus === 'refunded' ? 'คืนแล้ว' : 'ยังไม่หัก')})</div>` : '<div>🔒 <strong>เงินประกัน:</strong> ไม่มี</div>'}
          </div>
        </div>

        <div class="glass-card" style="background: #fff5f5; border: 1px solid #fed7d7;">
          <h4 style="font-weight: 700; color: #b91c1c; margin-bottom: 15px;">⚖️ แฟ้มประวัติทางวินัย (พ.ร.บ. คุ้มครองแรงงาน)</h4>
          <div style="margin-bottom: 15px; max-height: 200px; overflow-y: auto;">
            ${disciplinaryHTML}
          </div>
          
          ${window.checkPermission('manage_disciplinary') ? `
          <div style="display: flex; gap: 10px; border-top: 1px solid #fed7d7; padding-top: 15px;">
            <button class="btn btn-warning" onclick="openDisciplinaryModal('${escapeJsParam(emp.id)}', 'warning')">📝 ออกหนังสือตักเตือน</button>
            <button class="btn btn-danger" style="animation: ${hasActiveWarning ? 'pulse 2s infinite' : 'none'};" onclick="openDisciplinaryModal('${escapeJsParam(emp.id)}', 'termination')">🚨 ออกหนังสือเลิกจ้าง</button>
          </div>
          ` : ''}
        </div>

        <div class="glass-card">
          <h4 style="font-weight: 700; color: var(--primary-dark); margin-bottom: 15px;">เอกสารประกอบการสมัครงาน (เช่น สำเนาบัตรประชาชน, วุฒิการศึกษา)</h4>
          <div id="attachments-container-list" style="margin-bottom: 15px;">
            ${attachmentsHTML}
          </div>
          
          ${window.checkPermission('manage_employee_files') ? `
            <div style="border-top: 1px solid var(--gray-300); padding-top: 15px;">
              <h5>➕ อัปโหลดเอกสารใหม่</h5>
              <div style="display: flex; gap: 10px; margin-top: 10px; flex-wrap: wrap;">
                <input type="text" id="doc-upload-name" placeholder="ชื่อเอกสาร (เช่น ทะเบียนบ้าน)" style="flex: 1; padding: 8px 12px; font-size: 0.85rem;">
                <input type="file" id="doc-upload-file" style="width: auto; padding: 4px; font-size: 0.85rem;">
                <button class="btn btn-primary btn-sm" onclick="uploadEmployeeDocTrigger('${escapeJsParam(emp.id)}')">💾 อัปโหลด (.webp / .pdf)</button>
              </div>
              <div id="upload-doc-loading" style="color: var(--primary); font-weight: 700; margin-top: 10px; display: none;">⏳ กำลังอัปโหลดและประมวลผลไฟล์ WebP...</div>
            </div>
          ` : ''}
        </div>
      </div>
    </div>
  `;
  modalOverlay.classList.add('active');
};

async function uploadEmployeeDocTrigger(empId) {
  const nameInput = document.getElementById('doc-upload-name');
  const fileInput = document.getElementById('doc-upload-file');
  const loading = document.getElementById('upload-doc-loading');

  const docName = nameInput.value.trim();
  const file = fileInput.files[0];

  if (!docName || !file) {
    toast.warning("โปรดกรอกชื่อเอกสารและเลือกไฟล์ก่อนอัปโหลด");
    return;
  }

  try {
    loading.style.display = 'block';
    await uploadEmployeeDocument(empId, docName, file);
    toast.success("อัปโหลดเอกสารสำเร็จ!");
    loading.style.display = 'none';
    document.getElementById('details-modal-overlay').classList.remove('active');

    // Refresh employee list in state before re-rendering viewEmployeeDetails
    state.employees = await getEmployees(state.activeCompanyId);

    viewEmployeeDetails(empId); // Reload
  } catch (err) {
    console.error(err);
    toast.error("เกิดข้อผิดพลาดในการอัปโหลดไฟล์: " + err.message);
    loading.style.display = 'none';
  }
};

// --- DISCIPLINARY ACTIONS (Requirement: Section 119 Warning/Termination) ---
function openDisciplinaryModal(empId, suggestedType = 'warning') {
  // close details modal temporarily
  document.getElementById('details-modal-overlay').classList.remove('active');

  let discModal = document.getElementById('disciplinary-modal-overlay');
  if (!discModal) {
    discModal = document.createElement('div');
    discModal.id = 'disciplinary-modal-overlay';
    discModal.className = 'modal-overlay';
    document.body.appendChild(discModal);
  }

  const emp = state.employees.find(e => e.id === empId);
  const today = new Date().toISOString().split('T')[0];

  discModal.innerHTML = `
    <div class="modal-container" style="max-width: 600px;">
      <div class="modal-header" style="background: ${suggestedType === 'termination' ? 'var(--danger)' : 'var(--warning)'}; color: ${suggestedType === 'warning' ? '#000' : '#fff'};">
        <h3>${suggestedType === 'termination' ? '🚨 ออกหนังสือเลิกจ้าง' : '📝 ออกหนังสือตักเตือน'}: ${escapeHtml(emp.realName)}</h3>
        <button class="btn-close-modal" style="color: inherit;" onclick="document.getElementById('disciplinary-modal-overlay').classList.remove('active'); document.getElementById('details-modal-overlay').classList.add('active');">&times;</button>
      </div>
      <div class="modal-body" style="display: flex; flex-direction: column; gap: 15px;">
        <div>
          <label class="form-label" style="font-weight: 700;">ประเภทหนังสือ:</label>
          <select id="disc-type" class="form-input" style="padding: 10px;">
            <option value="warning" ${suggestedType === 'warning' ? 'selected' : ''}>หนังสือตักเตือนเป็นลายลักษณ์อักษร</option>
            <option value="termination" ${suggestedType === 'termination' ? 'selected' : ''}>หนังสือบอกเลิกจ้าง (ไม่ต้องจ่ายชดเชยตาม ม.119)</option>
          </select>
        </div>
        <div>
          <label class="form-label" style="font-weight: 700;">วันที่ออกเอกสาร (มีผลทันที):</label>
          <input type="date" id="disc-date" class="form-input" value="${today}" style="padding: 10px;">
        </div>
        <div>
          <label class="form-label" style="font-weight: 700;">สาเหตุการกระทำผิด (ระบุให้ชัดเจน):</label>
          <textarea id="disc-reason" class="form-input" rows="4" placeholder="เช่น ขาดงานติดต่อกัน 3 วันทำงานโดยไม่มีเหตุอันสมควร / มาสายเป็นประจำ / ทะเลาะวิวาท / ฯลฯ" style="padding: 10px; resize: vertical;"></textarea>
        </div>
        <button class="btn btn-primary" onclick="saveDisciplinaryRecord('${emp.id}')" style="width: 100%; margin-top: 10px; padding: 12px; font-size: 1.1rem;">💾 บันทึกประวัติและสร้างเอกสาร</button>
      </div>
    </div>
  `;

  discModal.classList.add('active');
};

async function saveDisciplinaryRecord(empId) {
  const type = document.getElementById('disc-type').value;
  const date = document.getElementById('disc-date').value;
  const reason = document.getElementById('disc-reason').value.trim();

  if (!date || !reason) {
    alert("กรุณากรอกข้อมูล วันที่ และ สาเหตุให้ครบถ้วน");
    return;
  }

  if (!confirm(`คุณยืนยันที่จะออกหนังสือ${type === 'warning' ? 'ตักเตือน' : 'เลิกจ้าง'} ใช่หรือไม่? ระบบจะบันทึกลงในแฟ้มประวัติพนักงานทันที`)) return;

  const emp = state.employees.find(e => e.id === empId);
  const newRecord = {
    id: 'disc_' + Date.now(),
    type: type,
    issueDate: date,
    reason: reason
  };

  const updatedRecords = emp.disciplinaryRecords ? [...emp.disciplinaryRecords, newRecord] : [newRecord];

  try {
    await updateEmployee(empId, { disciplinaryRecords: updatedRecords });

    // Update local state
    emp.disciplinaryRecords = updatedRecords;

    // Close disc modal, open details modal and re-render
    document.getElementById('disciplinary-modal-overlay').classList.remove('active');

    // Print the letter automatically
    printDisciplinaryLetter(empId, newRecord.id);

    viewEmployeeDetails(empId);
    renderEmployeesPage(); // Refresh the grid to show orange/red borders
  } catch (err) {
    console.error(err);
    alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล: " + err.message);
  }
};

async function deleteDisciplinaryRecord(empId, recordId) {
  const confirmed = await showConfirm({
    title: 'ยืนยันการลบประวัติ',
    message: 'คุณแน่ใจว่าต้องการลบประวัติการออกหนังสือฉบับนี้หรือไม่? การลบจะไม่สามารถกู้คืนได้',
    confirmText: '🗑️ ลบประวัติ',
    isDanger: true
  });
  if (!confirmed) return;

  const emp = state.employees.find(e => e.id === empId);
  if (!emp || !emp.disciplinaryRecords) return;

  const updatedRecords = emp.disciplinaryRecords.filter(r => r.id !== recordId);

  try {
    await updateEmployee(empId, { disciplinaryRecords: updatedRecords });

    // Update local state
    emp.disciplinaryRecords = updatedRecords;

    // Reload UI
    viewEmployeeDetails(empId);
    renderEmployeesPage(); // Refresh borders
    toast.success("ลบประวัติสำเร็จ!");
  } catch (err) {
    console.error(err);
    toast.error("เกิดข้อผิดพลาดในการลบข้อมูล: " + err.message);
  }
};

function printDisciplinaryLetter(empId, recordId) {
  const emp = state.employees.find(e => e.id === empId);
  if (!emp || !emp.disciplinaryRecords) return;

  const record = emp.disciplinaryRecords.find(r => r.id === recordId);
  if (!record) return;

  const comp = state.activeCompanySettings;
  const companyName = comp ? comp.companyName : 'บริษัท ------------------------';

  let title = record.type === 'warning' ? 'หนังสือตักเตือนเป็นลายลักษณ์อักษร' : 'หนังสือบอกเลิกจ้าง';
  let bodyHTML = '';

  const issueDateObj = new Date(record.issueDate);
  const thaiMonths = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
  const formattedDate = `${issueDateObj.getDate()} ${thaiMonths[issueDateObj.getMonth()]} ${issueDateObj.getFullYear() + 543}`;

  if (record.type === 'warning') {
    bodyHTML = `
      <p><strong>เรื่อง:</strong> ตักเตือนเป็นลายลักษณ์อักษร</p>
      <p><strong>เรียน:</strong> คุณ${escapeHtml(emp.realName)} (รหัสพนักงาน/เลขบัตรประชาชน: ${escapeHtml(emp.citizenId || '-')})</p>
      <br>
      <p style="text-indent: 40px;">ตามที่ท่านในฐานะพนักงานของ ${escapeHtml(companyName)} ได้กระทำผิดระเบียบข้อบังคับเกี่ยวกับการทำงาน หรือฝ่าฝืนคำสั่งอันชอบด้วยกฎหมายของนายจ้าง โดยมีรายละเอียดคือ:</p>
      <p style="margin: 20px 40px; padding: 15px; background: #f9f9f9; border-left: 4px solid #333; font-style: italic;">"${escapeHtml(record.reason)}"</p>
      <p style="text-indent: 40px;">การกระทำดังกล่าวถือเป็นการกระทำผิดระเบียบข้อบังคับของบริษัทอย่างชัดเจน บริษัทฯ จึงขอออกหนังสือฉบับนี้เพื่อ <strong>ตักเตือนเป็นลายลักษณ์อักษร</strong> ให้ท่านปรับปรุงพฤติกรรม และไม่กระทำผิดซ้ำอีก</p>
      <p style="text-indent: 40px;">หากท่านยังคงฝ่าฝืน หรือกระทำผิดซ้ำคำเตือนนี้อีกภายในระยะเวลา 1 ปี นับแต่วันที่ระบุในหนังสือฉบับนี้ บริษัทฯ จะพิจารณาลงโทษขั้นสูงสุด คือ <strong>เลิกจ้างโดยไม่จ่ายค่าชดเชย</strong> ตามมาตรา 119 แห่งพระราชบัญญัติคุ้มครองแรงงาน พ.ศ. 2541</p>
      <p style="text-indent: 40px;">จึงเรียนมาเพื่อทราบและปฏิบัติตามอย่างเคร่งครัด</p>
    `;
  } else {
    bodyHTML = `
      <p><strong>เรื่อง:</strong> เลิกจ้างโดยไม่จ่ายค่าชดเชย</p>
      <p><strong>เรียน:</strong> คุณ${escapeHtml(emp.realName)} (รหัสพนักงาน/เลขบัตรประชาชน: ${escapeHtml(emp.citizenId || '-')})</p>
      <br>
      <p style="text-indent: 40px;">ตามที่ท่านในฐานะพนักงานของ ${escapeHtml(companyName)} ได้กระทำความผิดทางวินัยอย่างร้ายแรง หรือกระทำผิดซ้ำคำเตือน ตามรายละเอียดดังนี้:</p>
      <p style="margin: 20px 40px; padding: 15px; background: #f9f9f9; border-left: 4px solid #333; font-style: italic;">"${escapeHtml(record.reason)}"</p>
      <p style="text-indent: 40px;">การกระทำดังกล่าวถือเป็นการฝ่าฝืนระเบียบข้อบังคับ หรือละทิ้งหน้าที่ตามที่ระบุใน <strong>มาตรา 119 แห่งพระราชบัญญัติคุ้มครองแรงงาน พ.ศ. 2541</strong> บริษัทฯ จึงขอแจ้งให้ท่านทราบว่า <strong>บริษัทฯ ขอเลิกจ้างท่าน โดยมีผลตั้งแต่วันที่ ${formattedDate} เป็นต้นไป และบริษัทฯ จะไม่จ่ายค่าชดเชยใดๆ ทั้งสิ้น</strong></p>
      <p style="text-indent: 40px;">ทั้งนี้ ขอให้ท่านส่งมอบงาน ตลอดจนทรัพย์สินของบริษัทฯ คืนให้กับฝ่ายบุคคล ภายในวันที่กำหนด</p>
      <p style="text-indent: 40px;">จึงเรียนมาเพื่อทราบ</p>
    `;
  }

  const printWindow = window.open('', '_blank', 'width=800,height=900');
  printWindow.document.write(`
    <!DOCTYPE html>
    <html lang="th">
    <head>
      <meta charset="UTF-8">
      <title>${escapeHtml(title)} - ${escapeHtml(emp.realName)}</title>
      <style>
        body { font-family: 'Sarabun', 'TH Sarabun New', sans-serif; padding: 40px; line-height: 1.6; color: #000; font-size: 16pt; }
        .header { text-align: center; margin-bottom: 40px; }
        .date { text-align: right; margin-bottom: 30px; }
        .signature-block { margin-top: 80px; display: flex; justify-content: space-between; }
        .signature-line { width: 250px; border-bottom: 1px dashed #000; margin-bottom: 10px; display: inline-block; }
        .text-center { text-align: center; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; margin: 0; padding: 20px; }
          .no-print { display: none !important; }
        }
      </style>
    </head>
    <body>
      <button class="no-print" onclick="window.print()" style="position: fixed; top: 10px; right: 10px; padding: 10px 20px; font-size: 14pt; background: #4CAF50; color: white; border: none; cursor: pointer; border-radius: 5px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">🖨️ พิมพ์เอกสาร (Ctrl+P)</button>
      
      <div class="header">
        <h2 style="margin:0; font-size: 24pt;">${escapeHtml(companyName)}</h2>
        <h3 style="margin: 10px 0; font-size: 20pt;">${escapeHtml(title)}</h3>
      </div>
      
      <div class="date">
        ทำที่: สำนักงานบริษัท<br>
        วันที่: ${formattedDate}
      </div>

      ${bodyHTML}

      <div class="signature-block">
        <div class="text-center">
          <br><br><br>
          <span class="signature-line"></span><br>
          ( ผู้บริหาร / ผู้มีอำนาจลงนาม )<br>
          ตัวแทนนายจ้าง
        </div>
        <div class="text-center">
          <br><br><br>
          <span class="signature-line"></span><br>
          ( ${escapeHtml(emp.realName)} )<br>
          พนักงานผู้รับทราบข้อกล่าวหา
        </div>
      </div>
      
      <script>
        // Auto trigger print dialog after small delay to ensure styles load
        setTimeout(() => { window.print(); }, 500);
      </script>
    </body>
    </html>
  `);
  printWindow.document.close();
};

async function removeEmployeeAttachment(empId, attachIndex) {
  const confirmed = await showConfirm({
    title: 'ยืนยันการลบเอกสาร',
    message: 'คุณแน่ใจว่าต้องการลบเอกสารนี้ถาวร? การลบจะไม่สามารถกู้คืนได้',
    confirmText: '🗑️ ยืนยันการลบ',
    isDanger: true
  });
  if (!confirmed) return;
  try {
    await deleteEmployeeAttachment(empId, attachIndex);
    toast.success("ลบเอกสารสำเร็จ!");
    document.getElementById('details-modal-overlay').classList.remove('active');

    // Refresh employee list in state before re-rendering viewEmployeeDetails
    const empIndex = state.employees.findIndex(e => e.id === empId);
    if (empIndex !== -1) {
      // Reload employees to ensure local state is updated
      state.employees = await getEmployees(state.activeCompanyId);
    }

    viewEmployeeDetails(empId); // Reload
  } catch (err) {
    console.error(err);
    toast.error("เกิดข้อผิดพลาดในการลบเอกสาร");
  }
};

function toggleBubbleMenu(btn) {
  const container = btn.closest('.bubble-menu-container');
  document.querySelectorAll('.bubble-menu-container').forEach(c => {
    if (c !== container) c.classList.remove('active');
  });
  container.classList.toggle('active');
};

async function downloadDocumentAs(url, filename, format) {
  if (format === 'webp') {
    // Just download directly or open URL in a new tab
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.webp`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return;
  }

  // Create beautiful progress overlay
  const overlay = document.createElement('div');
  overlay.id = 'download-progress-overlay';
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.width = '100vw';
  overlay.style.height = '100vh';
  overlay.style.background = 'rgba(0, 0, 0, 0.45)';
  overlay.style.backdropFilter = 'blur(6px)';
  overlay.style.display = 'flex';
  overlay.style.alignItems = 'center';
  overlay.style.justifyContent = 'center';
  overlay.style.zIndex = '1500';

  overlay.innerHTML = `
    <div class="glass-card" style="background:#fff; color:var(--text-main); padding:30px; border-radius:var(--border-radius-md); text-align:center; min-width:320px; box-shadow: 0 20px 40px rgba(0,0,0,0.3); border: 1px solid var(--gray-300); display:flex; flex-direction:column; align-items:center; gap:15px; font-family:inherit;">
      <h4 style="font-weight:700; color:var(--primary-dark); margin:0; font-size:1.1rem;">⏳ ระบบกำลังแปลงเอกสารเป็น ${format.toUpperCase()}</h4>
      
      <!-- Progress Bar Container -->
      <div style="width:100%; background:var(--gray-300); height:10px; border-radius:5px; overflow:hidden; position:relative;">
        <div id="download-progress-bar" style="width:10%; background:linear-gradient(90deg, var(--primary), var(--primary-dark)); height:100%; transition:width 0.4s ease; border-radius:5px;"></div>
      </div>
      
      <p id="download-progress-status" style="font-size:0.85rem; color:var(--text-sub); margin:0;">1. เริ่มต้นเชื่อมต่อดาวน์โหลดไฟล์...</p>
    </div>
  `;
  document.body.appendChild(overlay);

  const progressBar = document.getElementById('download-progress-bar');
  const progressStatus = document.getElementById('download-progress-status');

  const updateProgress = (percentage, statusText) => {
    progressBar.style.width = `${percentage}%`;
    progressStatus.textContent = statusText;
  };

  let blobUrl = null;
  try {
    const scriptUrl = (state.activeCompanySettings && state.activeCompanySettings.googleAppsScriptUrl)
      ? state.activeCompanySettings.googleAppsScriptUrl
      : 'https://script.google.com/macros/s/AKfycbzbMpQ7_dM6OY7QzxHaTHrem3ABx4K0uDIUGKM_A8qR9P3pvIxdJNF8Bl7rujQpQDJcZQ/exec';
    // Fetch blob securely (bypasses CORS for Firebase and Google Drive)
    updateProgress(35, "2. กำลังดาวน์โหลดไฟล์จากคลังเก็บข้อมูล...");
    const blob = await getAttachmentBlob(url, scriptUrl);
    blobUrl = URL.createObjectURL(blob);

    updateProgress(65, `3. กำลังประมวลผลไฟล์ภาพเป็น ${format.toUpperCase()}...`);
    const img = new Image();
    img.src = blobUrl;
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = () => reject(new Error("ไม่สามารถประมวลผลไฟล์รูปภาพจาก Blob ได้"));
    });

    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);

    updateProgress(90, `4. กำลังรวบรวมไฟล์และดาวน์โหลดลงเครื่องของคุณ...`);
    if (format === 'jpg') {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${filename}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else if (format === 'pdf') {
      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'l' : 'p',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height);
      pdf.save(`${filename}.pdf`);
    }

    updateProgress(100, `✅ แปลงไฟล์สำเร็จแล้ว!`);
    setTimeout(() => {
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay);
      }
    }, 800);
  } catch (err) {
    console.error(err);
    alert("ขออภัย! เกิดข้อผิดพลาดขณะแปลงและดาวน์โหลดไฟล์: " + err.message);
    if (document.body.contains(overlay)) {
      document.body.removeChild(overlay);
    }
  } finally {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
    }
  }
};

// Global click listener to close open bubble menus
if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.bubble-menu-container')) {
      document.querySelectorAll('.bubble-menu-container').forEach(c => c.classList.remove('active'));
    }
  });
}



// Bind all employee functions to window for backwards compatibility
if (typeof window !== 'undefined') {
  window.renderEmployeesPage = renderEmployeesPage;
  window.handleEmpDragStart = handleEmpDragStart;
  window.handleEmpDragOver = handleEmpDragOver;
  window.handleEmpDragLeave = handleEmpDragLeave;
  window.handleEmpDrop = handleEmpDrop;
  window.handleEmpDragEnd = handleEmpDragEnd;
  window.switchEmployeeTab = switchEmployeeTab;
  window.toggleReorderMode = toggleReorderMode;
  window.openEmployeeTimesheetPage = openEmployeeTimesheetPage;
  window.showAddEmployeeModal = showAddEmployeeModal;
  window.editEmployee = editEmployee;
  window.toggleCustomTaxRate = toggleCustomTaxRate;
  window.toggleCustomSSRate = toggleCustomSSRate;
  window.toggleInternStatus = toggleInternStatus;
  window.toggleDiligenceFields = toggleDiligenceFields;
  window.toggleCustomDiligenceAmount = toggleCustomDiligenceAmount;
  window.renderEmployeeFormModal = renderEmployeeFormModal;
  window.toggleEmployeeTypeFields = toggleEmployeeTypeFields;
  window.toggleSecurityDepositFields = toggleSecurityDepositFields;
  window.toggleLoanFields = toggleLoanFields;
  window.toggleResignFields = toggleResignFields;
  window.toggleEmployeeStatusFields = toggleEmployeeStatusFields;
  window.toggleLoginAccountFields = toggleLoginAccountFields;
  window.addNewRecurringRow = addNewRecurringRow;
  window.addTimesheetAdjustmentRow = addTimesheetAdjustmentRow;
  window.closeEmployeeModal = closeEmployeeModal;
  window.submitEmployeeForm = submitEmployeeForm;
  window.showResignedArchiveModal = showResignedArchiveModal;
  window.triggerHardDeleteEmployee = triggerHardDeleteEmployee;
  window.viewEmployeeDetails = viewEmployeeDetails;
  window.uploadEmployeeDocTrigger = uploadEmployeeDocTrigger;
  window.openDisciplinaryModal = openDisciplinaryModal;
  window.saveDisciplinaryRecord = saveDisciplinaryRecord;
  window.deleteDisciplinaryRecord = deleteDisciplinaryRecord;
  window.printDisciplinaryLetter = printDisciplinaryLetter;
  window.removeEmployeeAttachment = removeEmployeeAttachment;
  window.toggleBubbleMenu = toggleBubbleMenu;
  window.downloadDocumentAs = downloadDocumentAs;
}

export {
  renderEmployeesPage,
  handleEmpDragStart,
  handleEmpDragOver,
  handleEmpDragLeave,
  handleEmpDrop,
  handleEmpDragEnd,
  switchEmployeeTab,
  toggleReorderMode,
  openEmployeeTimesheetPage,
  showAddEmployeeModal,
  editEmployee,
  toggleCustomTaxRate,
  toggleCustomSSRate,
  toggleInternStatus,
  toggleDiligenceFields,
  toggleCustomDiligenceAmount,
  renderEmployeeFormModal,
  toggleEmployeeTypeFields,
  toggleSecurityDepositFields,
  toggleLoanFields,
  toggleResignFields,
  toggleEmployeeStatusFields,
  toggleLoginAccountFields,
  addNewRecurringRow,
  addTimesheetAdjustmentRow,
  closeEmployeeModal,
  submitEmployeeForm,
  showResignedArchiveModal,
  triggerHardDeleteEmployee,
  viewEmployeeDetails,
  uploadEmployeeDocTrigger,
  openDisciplinaryModal,
  saveDisciplinaryRecord,
  deleteDisciplinaryRecord,
  printDisciplinaryLetter,
  removeEmployeeAttachment,
  toggleBubbleMenu,
  downloadDocumentAs
};
